// The shared dialog.
//
// Ported from Zanetto Tools (phase 7 stage 2). Almost every comment below
// records a bug that was found in production there, which is the whole reason
// this is a port rather than a fresh dialog.
import { useEffect, useId, useRef, type ReactNode } from 'react';

/**
 * Every open dialog, oldest first. Dialogs genuinely stack — a confirmation
 * raised from inside the schedule day panel puts a second one on top of the
 * first — and both would otherwise listen on `document` at the same time, so
 * one Escape closed both and the two focus traps fought over Tab.
 */
const stack: string[] = [];
const isTopmost = (id: string) => stack[stack.length - 1] === id;

export interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Off by default on purpose — see the note below. Pass it for read-only
   * dialogs, never for one holding a form.
   */
  dismissOnBackdrop?: boolean;
  /** `lg` is the default; `xl` for the day panel, `2xl` for the document viewer. */
  size?: 'lg' | 'xl' | '2xl';
}

const SIZE = {
  lg: 'max-w-lg',
  xl: 'max-w-3xl',
  '2xl': 'max-w-5xl',
} as const;

/**
 * Dismissal is deliberately asymmetric and consistent app-wide:
 *   - Escape closes the topmost dialog only.
 *   - The × always closes.
 *   - Clicking the backdrop closes ONLY when `dismissOnBackdrop` is set, which
 *     callers pass for read-only dialogs. A form dialog that closed on a stray
 *     backdrop click would throw away typed work, which is why it is off by
 *     default.
 *
 * Focus is trapped while open and returned to whatever was focused before, so
 * keyboard and screen-reader users are not dumped back at the top of the page.
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
  dismissOnBackdrop = false,
  size = 'lg',
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Read through a ref so the effect below can set up once and stay put.
  // Callers pass a fresh `onClose` arrow on every render (`onClose={() => …}`),
  // so listing it as a dependency re-ran the whole effect on every keystroke:
  // the cleanup restored focus to whatever opened the dialog and the setup then
  // parked it on the panel, which emptied the field you were typing in after a
  // single character. The effect must not depend on anything that changes per
  // render — put new values here, not in the dependency array.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    stack.push(titleId);

    // If nothing inside the dialog has claimed focus (autoFocus on a field,
    // say), put it on the panel itself. Without this a dialog opened from a
    // button that then unmounts — every row-level Delete — leaves focus on
    // <body>, so the trap has nothing to trap and Tab starts again from the
    // top of the page.
    if (panelRef.current && !panelRef.current.contains(document.activeElement)) {
      const target =
        panelRef.current.querySelector<HTMLElement>('[autofocus]') ?? panelRef.current;
      target.focus();
    }

    const handler = (event: KeyboardEvent) => {
      if (!isTopmost(titleId)) return;

      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;

      // Focus trap. Without this, tabbing walks out of the dialog and into the
      // page behind it, which is still visible through the backdrop.
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (!panelRef.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      const i = stack.indexOf(titleId);
      if (i !== -1) stack.splice(i, 1);
      if (previouslyFocused instanceof HTMLElement && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [titleId]);

  // Below `sm` the dialog is a full-screen sheet (phase 8, DM2): the panel
  // stretches to the whole viewport, the title bar and footer stick to its
  // edges, and only the body scrolls between them — so on a long form the
  // Save button is always on screen, not below two screens of fields. The
  // outer container stays the scroll parent on every size, which is what the
  // sticky positioning hangs off.
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-stretch justify-center sm:items-center sm:p-4">
        <div
          className="fixed inset-0 bg-gray-900/50 transition-opacity"
          onClick={dismissOnBackdrop ? onClose : undefined}
          aria-hidden="true"
        />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          // Focusable programmatically but not by Tab — it is only ever the
          // fallback landing spot set above.
          tabIndex={-1}
          className={`relative flex w-full flex-col bg-white shadow-xl focus:outline-none sm:rounded-lg ${SIZE[size]}`}
        >
          {/* Sticky needs its own fill, or the scrolled body shows through. */}
          <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-gray-200 bg-white px-5 py-3 sm:static sm:rounded-t-lg">
            <h2 id={titleId} className="text-base font-semibold text-gray-900">
              {title}
            </h2>
            <button type="button" onClick={onClose} aria-label="Close dialog" className="btn-icon -mr-1.5">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* flex-1 pushes the footer to the bottom of the phone sheet; on
              desktop the panel is only as tall as its content, so it is inert. */}
          <div className="flex-1 px-5 py-4">{children}</div>

          {footer && (
            // The safe-area padding keeps the buttons clear of the home
            // indicator in the installed PWA, same as the tab bar.
            <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:static sm:rounded-b-lg sm:pb-3">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
